import { useState } from 'react';
import { Button } from '../../ui/Button/Button';
import { EmptyState } from '../../ui/State/EmptyState';
import { copyText } from '../clipboard';
import { DEMO_SELECTION } from '../config';
import { goToDataset } from '../dataset';
import { testId } from '../testId';
import styles from './liveLook.module.css';

// T-283 — the words of the live look, and the two surfaces that carry the one command.
//
// The live look (T-282, D-362) has exactly one moving part a reader can act on: `node tools/meta-pull.mjs
// --preview` reads the real account through the Graph API (GET only), writes the dataset outside the repo and
// copies it beside the founder preview. Everything else about it is a fact to be stated, not a control.
//
// So this file holds (a) that command, in ONE place, with a copy affordance that tells the truth about whether
// the copy worked (`app/clipboard.ts`), (b) the designed state for "`?dataset=live` was asked for and there is
// nothing beside the app", and (c) the recognition of the loader's error, by NAME rather than by class, so the
// error boundary does not drag the loader (and the `live/` fetches) into the main chunk.
//
// Nothing here can reach Meta: the app has no transport to it at all. The command is text on a screen for a
// person to run in their own terminal, and the numeric ad account id appears in none of it (HARD RULE 2).

export const PULL_COMMAND = 'node tools/meta-pull.mjs --preview';

export const LIVE_LOOK_COPY = {
  /** The missing-dataset state (§12 no. 27's first-use empty state: what will appear here, and the next step). */
  missingTitle: 'No live account has been pulled yet',
  missingBody:
    'The live look shows your own ad account exactly as the last pull read it, and nothing has been pulled beside this app. Run the pull once and reload — it only reads, so nothing about your account changes.',
  missingAction: 'Use the demo instead',
  /** The "Connect your account" sheet, offered by the switch when there is no live entry to list. */
  connect: 'Connect your account',
  connectTitle: 'Show your own account',
  /**
   * Two parts, because the dialog's header is not a paragraph's place. `subtitle` sits in the header beside the
   * mode chip, which costs it most of a 420 px row (D-172's measurement, in the sheet's words: a chip must not
   * take the sentence's width) — so the header keeps ONE line, which is also the dialog's description, and the
   * explanation runs edge to edge in the body above the command.
   */
  connectLead: 'One command puts your own ad account beside this app, read-only.',
  connectBody:
    'It reads your campaigns, your spend and your results through Meta, writes them to this machine, and sends nothing back. AdOrchestrator cannot change your ads.',
  connectClose: 'Close',
  /** What the command block itself says. */
  commandLabel: 'Run this once, then reload',
  commandHint: 'It runs on this machine and takes a few minutes the first time.',
  copy: 'Copy command',
  copied: 'Copied.',
  copyFailed: 'Could not copy — select the line and copy it.',
} as const;

/** The loader's failure, recognised without importing it: `LiveDatasetError` sets its own `name`. */
export function isLiveDatasetError(error: unknown): boolean {
  return error instanceof Error && error.name === 'LiveDatasetError';
}

/**
 * The one command, with a copy button that reports what actually happened. The status line is `role="status"`
 * so a screen reader hears the result of a press that moves no focus and changes nothing on screen.
 */
export function PullCommand({ testID }: { testID: string }) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <div className={styles.command} data-testid={testID}>
      <span className={styles.commandLabel}>{LIVE_LOOK_COPY.commandLabel}</span>
      <code className={styles.commandLine} data-testid={testId(testID, 'line')}>
        {PULL_COMMAND}
      </code>
      <div className={styles.commandFoot}>
        <Button
          variant="ghost"
          size="sm"
          icon="copy"
          testID={testId(testID, 'copy')}
          onClick={() => {
            void copyText(PULL_COMMAND).then((ok) =>
              setSaid(ok ? LIVE_LOOK_COPY.copied : LIVE_LOOK_COPY.copyFailed),
            );
          }}
        >
          {LIVE_LOOK_COPY.copy}
        </Button>
        <span className={styles.commandHint} role="status" data-testid={testId(testID, 'said')}>
          {said ?? LIVE_LOOK_COPY.commandHint}
        </span>
      </div>
    </div>
  );
}

/**
 * `?dataset=live`, and there is no dataset beside the app — **the designed state, not the generic boundary.**
 *
 * Before this, the loader's `LiveDatasetError` landed in `RouteErrorBoundary` and read "Something went wrong on
 * this page / try again" over a console hint nobody outside a devtools tab sees. Nothing went wrong: a file the
 * reader has not created yet is not there, and the honest screen says what is missing, the one command that
 * makes it, and the way back to the demo. There is no "Try again": the address is fine and retrying it would
 * fail the same way — the only thing that helps is the pull, and then a reload.
 *
 * It has no mode chip because there is no account and therefore no mode: it stands in for the whole app, which
 * is the same reason the stale-build screen has none (REQ-006 AC5 is about surfaces that COVER a signed-in
 * screen).
 */
export function NoLiveDatasetScreen() {
  return (
    <main className={styles.page} data-testid={testId('state', 'no-live-dataset')}>
      <div className={styles.card}>
        <EmptyState
          icon="wallet"
          headingLevel={2}
          title={LIVE_LOOK_COPY.missingTitle}
          body={LIVE_LOOK_COPY.missingBody}
          testID={testId('state', 'no-live-dataset', 'empty')}
          action={
            <Button
              variant="primary"
              icon="flask"
              testID={testId('state', 'no-live-dataset', 'demo')}
              onClick={() => goToDataset(DEMO_SELECTION)}
            >
              {LIVE_LOOK_COPY.missingAction}
            </Button>
          }
        />
        <PullCommand testID={testId('state', 'no-live-dataset', 'command')} />
      </div>
    </main>
  );
}
